import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import {
  Send,
  UserX,
  Pencil,
  ThumbsDown,
  RefreshCw,
  CheckCircle2,
  PhoneOff,
  CalendarPlus,
  CalendarClock,
  Repeat,
  XCircle,
  EyeOff,
  CheckCheck,
  Forward,
  Star,
  Zap,
  Loader2,
} from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { DropdownMenuItem } from 'src/components/ui/dropdown-menu';
import { useExecuteFlowAction } from 'src/services/api/mutations/candidateMutations';
import { useToast } from 'src/hooks/use-toast';
import { Alert } from 'src/components/common/Alert';
import { cn } from 'src/lib/utils';
import { getToneButtonClasses } from 'src/lib/statusStyles';
import { registerAction } from './actionRegistry';
import { ACTION_PAYLOAD_SCHEMAS } from './actionPayloadSchemas';
import { getActionScheme } from './actionColors';
import ActionPayloadModal from './ActionPayloadModal';
import UpdateProfileDetailsModal from './UpdateProfileDetailsModal';

// Action keys whose payload needs a bespoke modal rather than the generic
// schema-driven one (ActionPayloadModal) — e.g. interview_profile_update's
// multi-select locations and split year/month experience fields don't fit
// the generic field-type renderer. Checked first; ACTION_PAYLOAD_SCHEMAS is
// the fallback for everything that does fit the generic shape.
//
// Key confirmed live via GET /assignments/:id/flow-statuses on the
// "Contacted" state: the backend's real key is interview_profile_update,
// not update_profile_details (this map's original name, carried over from
// the planning doc before the endpoint was live) — same class of
// sample-vs-live drift this flow has already hit twice (status→state,
// assignment_locations→requirement_locations). Registering under the wrong
// key doesn't error — getActionComponent() just returns nothing for it, so
// RowActionsCell's own "filter to components that resolved" silently drops
// the action from the menu instead of rendering a broken one. Caught by
// that exact silence: the kebab on a live Contacted-tab row was missing
// this action entirely.
const CUSTOM_MODALS = {
  interview_profile_update: UpdateProfileDetailsModal,
};

// One generic, data-driven action component for the "senior_level_flow"
// state machine — per the "Flow State Management" API guide (PR #748):
// GET .../flow-statuses returns each status's own manual_actions list, and
// every action fires through the SAME endpoint (POST /internal/candidates/
// {sec_mapping_id}/flow-actions/{action_key}), not a bespoke one per key
// the way `default` flow's assign_test does. So instead of ~15 near-
// identical bespoke components, this one component is registered under
// every key below — `actionKey` (passed in by ManualActionsBar.jsx) is what
// tells a given render which one it is.
//
// The endpoint is single-candidate (sec_mapping_id in the path, not a
// mapping_ids array), so a multi-select bulk action here becomes N
// individual calls via Promise.allSettled, then one aggregated toast —
// same success/mixed/failure shape AssignTestAction.jsx already uses for
// its own (different) bulk endpoint.
//
// One icon per action key, chosen for what the action actually does rather
// than a single generic bolt for all fifteen. Falls back to Zap for any
// key the backend adds before this map is updated for it.
const ACTION_ICONS = {
  send_contact_link: Send,
  remove_from_pipeline: UserX,
  interview_profile_update: Pencil,
  mark_not_interested: ThumbsDown,
  resend_contact_link: RefreshCw,
  acknowledge_contact: CheckCircle2,
  mark_as_non_responsive: PhoneOff,
  assign_slot: CalendarPlus,
  reschedule_slot: CalendarClock,
  acknowledge_interview: CheckCircle2,
  resend_test_link: Repeat,
  resend_assessment_link: Repeat,
  reject: XCircle,
  mark_as_no_show: EyeOff,
  complete_interview_round: CheckCheck,
  forward_to_cse: Forward,
  move_to_shortlisted: Star,
};

const SENIOR_LEVEL_FLOW_ACTION_KEYS = Object.keys(ACTION_ICONS);

// Whether this action key needs a payload-collection modal before it can
// fire — exported so RowActionsCell.jsx can decide, per resolved action,
// whether it needs to mount a modal-hosting instance at all (see below).
export const actionNeedsPayload = (actionKey) =>
  !!ACTION_PAYLOAD_SCHEMAS[actionKey] || !!CUSTOM_MODALS[actionKey];

// The API guide calls out 400s specifically ("action not allowed in current
// status," or the action itself failing) — surfacing the real backend
// message matters more here than for a single-purpose action, since a user
// could plausibly hit this from a stale tab.
const extractErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || 'Action failed';

// renderAs:
//   'button'    — bulk-action bar (ManualActionsBar.jsx). Self-contained:
//                 renders the button AND owns the modal's open state itself.
//   'menuItem'  — row kebab menu trigger (RowActionsCell.jsx). Renders ONLY
//                 the DropdownMenuItem — no modal here. See modalOnly below
//                 for why.
//   'modalOnly' — row kebab's modal host, rendered by RowActionsCell as a
//                 SIBLING of its <DropdownMenu>, not a descendant of
//                 <DropdownMenuContent>. Renders only the modal, fully
//                 controlled via the modalOpen/onModalOpenChange props.
//
// Why menuItem and modalOnly are split rather than one component doing both
// (as 'button' does): a Radix Dialog/Sheet opened synchronously from inside
// a DropdownMenuItem's click handler, while still nested inside that
// dropdown's own DropdownMenuContent, gets unmounted the instant Radix
// closes the dropdown — the whole subtree, modal included, dies with it.
// Confirmed live: the identical trigger via a plain Button (not inside a
// dropdown, the 'button' path) opened its modal fine; the DropdownMenuItem
// one didn't; a setTimeout deferral didn't help either, since the component
// instance itself (not just a synchronous focus race) unmounts. Only
// moving the modal to a stable location outside the dropdown's own subtree
// fixes it — RowActionsCell owns the shared open/actionKey state and
// renders the 'modalOnly' instance there.
const GenericFlowAction = ({
  actionKey,
  mappingIds,
  candidateIds,
  opportunityId,
  assignmentId,
  label,
  onSuccess,
  renderAs,
  modalOpen,
  onModalOpenChange,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const executeFlowAction = useExecuteFlowAction();
  const Icon = ACTION_ICONS[actionKey] || Zap;
  // Per-action color — see actionColors.js for the actionKey → scheme
  // mapping and src/lib/statusStyles.js's TONE_BUTTON_CLASSES for what each
  // scheme actually renders as. Falls back to the neutral 'gray' tone for
  // any action key not yet categorized there.
  const tone = getToneButtonClasses(getActionScheme(actionKey));
  // A handful of action keys (e.g. assign_slot — see actionPayloadSchemas.js
  // — or interview_profile_update, which needs its own bespoke modal) need
  // structured input beyond "which candidates" before they can fire. When
  // one applies, the trigger opens a modal to collect it instead of firing
  // immediately.
  const payloadSchema = ACTION_PAYLOAD_SCHEMAS[actionKey];
  const CustomModal = CUSTOM_MODALS[actionKey];
  const needsPayload = !!payloadSchema || !!CustomModal;
  // 'button' owns its own state (uncontrolled) and renders the modal itself.
  // 'modalOnly' renders the modal too, fully controlled by RowActionsCell's
  // state. 'menuItem' never renders a modal — but when it needs one, its
  // click has to signal the SAME external state 'modalOnly' reads, not a
  // local one that would unmount along with the dropdown before it's ever
  // read (that was the original bug).
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlsExternally =
    renderAs === 'modalOnly' || renderAs === 'menuItem';
  const open = renderAs === 'modalOnly' ? modalOpen : uncontrolledOpen;
  const setOpen = controlsExternally ? onModalOpenChange : setUncontrolledOpen;

  const disabled = mappingIds.length === 0 || executeFlowAction.isPending;

  // onSuccess() is what refetches the candidate list. For a save that keeps
  // the modal open (Details tab, closeOnSubmit: false), firing it right away
  // is exactly what breaks the "stay open, switch to Skills" behaviour: the
  // refetch lands the candidate in a new status, they drop off this tab, the
  // row unmounts — and this modal, hosted by that row's RowActionsCell
  // (renderAs="modalOnly"), unmounts with it. So a deferred save records
  // that a refetch is owed and flushes it only once the modal actually
  // closes (handleModalOpenChange below).
  const deferredSuccessRef = useRef(false);

  const runAction = async (payload, { deferSuccess = false } = {}) => {
    const results = await Promise.allSettled(
      mappingIds.map((secMappingId) =>
        executeFlowAction.mutateAsync({
          secMappingId,
          actionKey,
          payload,
          // The mutation invalidates the candidate list on success by
          // default; a deferred save suppresses that too (not just the
          // onSuccess prop below) — handleModalOpenChange flushes both.
          skipListInvalidate: deferSuccess,
        })
      )
    );
    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    const failures = results.filter((r) => r.status === 'rejected');
    const failureCount = failures.length;

    if (failureCount === 0) {
      toast({
        message: `${label} — done for ${successCount} candidate${successCount === 1 ? '' : 's'}`,
        variant: 'success',
      });
    } else if (successCount === 0) {
      toast({
        message: extractErrorMessage(failures[0].reason),
        variant: 'destructive',
      });
    } else {
      toast({
        message: `${label}: ${successCount} succeeded, ${failureCount} failed — ${extractErrorMessage(failures[0].reason)}`,
        variant: 'destructive',
      });
    }
    if (deferSuccess) deferredSuccessRef.current = true;
    else onSuccess();
  };

  // Every action opens a modal before it fires now — a payload form for the
  // handful that need structured input, a plain yes/no confirmation for
  // everything else. These fire on a single click (kebab item or bulk bar
  // button) with no undo, so a stray click on e.g. Reject or Remove from
  // Pipeline shouldn't be able to act on a candidate without a chance to
  // back out.
  const handleClick = () => {
    if (mappingIds.length === 0) {
      toast({ message: 'Select at least one candidate', variant: 'warning' });
      return;
    }
    setOpen(true);
  };

  // Every close path (Cancel, the X, a submit that closes, the Skills tab's
  // own post-save close) routes through here so a deferred refetch always
  // gets flushed — after the modal is on its way out, where landing the
  // candidate on a new tab no longer matters.
  const handleModalOpenChange = (next) => {
    if (!next && deferredSuccessRef.current) {
      deferredSuccessRef.current = false;
      // Both halves of what a normal save would have done immediately: the
      // broad list invalidation the mutation skipped, and the page's own
      // refetch (onSuccess === AssignmentPipeline's refetchCandidates).
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      onSuccess();
    }
    setOpen(next);
  };

  // closeOnSubmit defaults true (every existing caller's behavior —
  // ActionPayloadModal calls onSubmit(payload) with no second arg, so it's
  // unaffected). UpdateProfileDetailsModal is the one caller that passes
  // { closeOnSubmit: false } — its Details tab's own save shouldn't close
  // the dialog, since the Skills tab underneath is a second, independent
  // save the TA still needs to get to (see that component's own comment).
  // A non-closing save also defers its onSuccess() refetch (see the
  // deferredSuccessRef comment) so it doesn't unmount the modal out from
  // under itself.
  const handleModalSubmit = async (payload, { closeOnSubmit = true } = {}) => {
    await runAction(payload, { deferSuccess: !closeOnSubmit });
    if (closeOnSubmit) handleModalOpenChange(false);
  };

  if (renderAs === 'menuItem') {
    // No modal rendered here — see the block comment above.
    return (
      <DropdownMenuItem
        onClick={handleClick}
        disabled={disabled}
        // Tinted to the action's own color (tone.text) rather than the
        // generic muted-foreground every menu item used to share — a light
        // matching background on hover/focus, not a solid fill (a kebab
        // full of solid-colored rows reads noisy), per the per-action color
        // scheme — see actionColors.js. DropdownMenuItem's own base class
        // (src/components/ui/dropdown-menu.jsx) sets `focus:text-accent-
        // foreground` — Radix moves real DOM focus onto a menu item on
        // pointer hover (not just keyboard nav), so that rule fires on a
        // plain mouse hover too. An explicit `focus:${tone.text}` is needed
        // to override it (confirmed live: without it, hovering any kebab
        // item showed accent-foreground/blue text regardless of this tone,
        // since only the background was being overridden, not the text).
        className={cn(
          'gap-2',
          tone.text,
          `focus:${tone.text}`,
          tone.hoverBg,
          // gray's own hoverBg carries two hover: classes (bg + text) — a
          // plain .replace only swaps the first, so this needs the global
          // flag to convert both to their focus: equivalent.
          tone.hoverBg.replace(/hover:/g, 'focus:')
        )}
      >
        <Icon className="h-4 w-4" />
        {label}
      </DropdownMenuItem>
    );
  }

  // Only meaningful for a single candidate — a bulk capture has no one
  // candidate's own profile/skill-match to prefill against, so these come
  // through as null and each modal falls back to its manual-entry-only
  // rendering (same gating pattern secMappingId below already established).
  const singleCandidateId = candidateIds.length === 1 ? candidateIds[0] : null;

  let modal = null;
  if (CustomModal) {
    modal = (
      <CustomModal
        open={open}
        onOpenChange={handleModalOpenChange}
        candidateCount={mappingIds.length}
        secMappingId={mappingIds.length === 1 ? mappingIds[0] : null}
        candidateId={singleCandidateId}
        opportunityId={opportunityId}
        assignmentId={assignmentId}
        isSubmitting={executeFlowAction.isPending}
        onSubmit={handleModalSubmit}
      />
    );
  } else if (payloadSchema) {
    modal = (
      <ActionPayloadModal
        open={open}
        onOpenChange={handleModalOpenChange}
        schema={payloadSchema}
        candidateCount={mappingIds.length}
        // Only meaningful for reschedule_slot's prefill (see
        // actionPayloadSchemas.js's `prefillFromSchedule`) — a bulk
        // multi-select fire (ManualActionsBar, renderAs="button") has no
        // single secMappingId to prefill against, same gating pattern
        // UpdateProfileDetailsModal already uses for skill-match.
        secMappingId={mappingIds.length === 1 ? mappingIds[0] : null}
        isSubmitting={executeFlowAction.isPending}
        onSubmit={handleModalSubmit}
      />
    );
  } else {
    // No structured input needed — just a guard against an accidental
    // click before it fires. Phrased generically (never trying to
    // conjugate `label` into a sentence) so it reads correctly for every
    // action key without a per-action copy override.
    modal = (
      <Alert
        isOpen={open}
        onClose={() => setOpen(false)}
        title={`Confirm ${label}`}
        description={`${label} for ${mappingIds.length} candidate${
          mappingIds.length === 1 ? '' : 's'
        }? This can't be undone.`}
        cancelText="Cancel"
        continueText={label}
        onCancel={() => {}}
        onContinue={() => runAction()}
      />
    );
  }

  if (renderAs === 'modalOnly') {
    return modal;
  }

  return (
    <>
      <Button
        variant="tone"
        size="sm"
        onClick={handleClick}
        disabled={disabled}
        // Tinted to the action's own color — see actionColors.js.
        // `variant="tone"` (not `outline`) deliberately carries no color of
        // its own — see button.jsx's own comment on why layering these
        // classes on top of `outline` left every hover text coming out
        // primary-blue regardless of this className. A scheme with its own
        // `solid` set (currently just emerald, per explicit request/
        // reference) renders as a filled button instead of the soft tonal
        // one — only here, not in the menuItem branch above, where a solid
        // row would look out of place next to plain tonal-text siblings.
        className={cn(
          'flex items-center gap-1 text-xs',
          tone.solid
            ? [tone.solid.bg, tone.solid.text, tone.solid.hoverBg]
            : [tone.bg, tone.text, tone.border, tone.hoverBg]
        )}
      >
        {executeFlowAction.isPending && !needsPayload ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Working...
          </>
        ) : (
          <>
            <Icon className="h-3.5 w-3.5" />
            {label}
          </>
        )}
      </Button>
      {modal}
    </>
  );
};

GenericFlowAction.propTypes = {
  actionKey: PropTypes.string,
  mappingIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  candidateIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  onSuccess: PropTypes.func,
  renderAs: PropTypes.oneOf(['button', 'menuItem', 'modalOnly']),
  modalOpen: PropTypes.bool,
  onModalOpenChange: PropTypes.func,
};

GenericFlowAction.defaultProps = {
  actionKey: undefined,
  mappingIds: [],
  candidateIds: [],
  opportunityId: undefined,
  assignmentId: undefined,
  label: 'Run action',
  onSuccess: () => {},
  renderAs: 'button',
  modalOpen: false,
  onModalOpenChange: () => {},
};

SENIOR_LEVEL_FLOW_ACTION_KEYS.forEach((actionKey) => {
  registerAction({
    stateMachineKey: 'senior_level_flow',
    actionKey,
    component: GenericFlowAction,
  });
});

export default GenericFlowAction;
